import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, ImagePlus, Loader2, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ImageCropper from "../../components/ImageCropper";
import Sheet from "../../components/Sheet";

const LOGO_PICK_MAX_BYTES = 30 * 1024 * 1024;
const LOGO_ASPECTS = [
  { label: "Square", value: 1 },
  { label: "Wide", value: 2 },
];

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

export default function AdminCompany() {
  const { refresh } = useAuth();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [saved, setSaved] = useState({ name: "", address: "", website: "" });

  const [cropSrc, setCropSrc] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/company");
      setLogo(data.logo_url || "");
      const values = { name: data.name || "", address: data.address || "", website: data.website || "" };
      setName(values.name);
      setAddress(values.address);
      setWebsite(values.website);
      setSaved(values);
    } catch (e) {
      toast.error("Could not load the company.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = name !== saved.name || address !== saved.address || website !== saved.website;

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG or WEBP).");
      return;
    }
    if (file.size > LOGO_PICK_MAX_BYTES) {
      toast.error("Image is too large (max 30 MB).");
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const saveLogo = async (blob) => {
    closeCrop();
    setLogoBusy(true);
    try {
      const dataUrl = await blobToDataUrl(blob);
      await api.patch("/company", { logo_url: dataUrl });
      setLogo(dataUrl);
      await refresh();
      toast.success("Logo updated");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.patch("/company", { logo_url: "" });
      setLogo("");
      setRemoveOpen(false);
      await refresh();
      toast.success("Logo removed");
    } catch (err) {
      toast.error("Could not remove the logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) {
      toast.error("The legal name cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/company", { name: name.trim(), address: address.trim(), website: website.trim() });
      setSaved({ name: name.trim(), address: address.trim(), website: website.trim() });
      setName(name.trim());
      setAddress(address.trim());
      setWebsite(website.trim());
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the profile.");
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-10 pt-5 md:px-8 md:pt-10">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Company Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your brand. The logo appears on inspection headers and on exported PDF reports.
        </p>
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="logo-file-input" />

      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading...</div>
      ) : (
        <>
          {/* Logo */}
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Brand logo</h2>
            <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="grid h-36 w-36 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-background">
                {logo ? (
                  <img src={logo} alt="Company logo" className="h-full w-full object-contain" />
                ) : (
                  <Building2 className="h-12 w-12 text-muted-foreground" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm text-muted-foreground">
                  PNG, JPG or WEBP. After you pick the image you can move and zoom it to frame it.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={logoBusy}
                    className="inline-flex h-12 items-center gap-2 rounded-lg border border-border bg-background px-4 font-semibold disabled:opacity-60"
                    data-testid="replace-logo-btn"
                  >
                    {logoBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                    {logo ? "Replace image" : "Upload image"}
                  </button>
                  {logo && (
                    <button
                      type="button"
                      onClick={() => setRemoveOpen(true)}
                      disabled={logoBusy}
                      className="inline-flex h-12 items-center gap-2 rounded-lg border border-red-500/50 px-4 font-semibold text-red-400 disabled:opacity-60"
                      data-testid="remove-logo-btn"
                    >
                      <Trash2 className="h-5 w-5" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Perfil */}
          <section className="mt-5 rounded-xl border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Company profile</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="co-name" className="mb-1.5 block text-sm font-medium">
                  Legal name
                </label>
                <input id="co-name" value={name} onChange={(e) => setName(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="co-address" className="mb-1.5 block text-sm font-medium">
                  Registered address
                </label>
                <input id="co-address" value={address} onChange={(e) => setAddress(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="co-website" className="mb-1.5 block text-sm font-medium">
                  Website
                </label>
                <input
                  id="co-website"
                  type="url"
                  inputMode="url"
                  autoCapitalize="none"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                  className={fieldClass}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={saveProfile}
              disabled={saving || !dirty}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black disabled:opacity-50 sm:ml-auto sm:w-auto sm:px-8"
              data-testid="save-profile-btn"
            >
              {saving && <Loader2 className="h-5 w-5 animate-spin" />}
              {saving ? "Saving..." : "Save profile"}
            </button>
          </section>
        </>
      )}

      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          shape="square"
          aspects={LOGO_ASPECTS}
          title="Brand logo"
          outputWidth={800}
          outputType="image/png"
          onCancel={closeCrop}
          onDone={saveLogo}
        />
      )}

      {removeOpen && (
        <Sheet
          title="Remove the logo?"
          subtitle="Reports and headers will go back to showing no logo."
          onClose={() => !logoBusy && setRemoveOpen(false)}
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRemoveOpen(false)}
                disabled={logoBusy}
                className="h-12 flex-1 rounded-xl border border-border text-base font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeLogo}
                disabled={logoBusy}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-destructive text-base font-semibold text-destructive-foreground disabled:opacity-60"
              >
                {logoBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                Remove
              </button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">You can upload a new one at any time.</p>
        </Sheet>
      )}
    </div>
  );
}
